/**
 * GlobalCommandPalette — Cmd+K / Ctrl+K universal search across the ecosystem.
 *
 * Mounted globally in App.js. Uses shadcn's `command.jsx` (cmdk) primitive
 * and calls the shared /api/search/universal endpoint. Debounced 250ms.
 *
 * Only renders for authenticated admins (no token → no palette).
 *
 * Quick Actions — a curated set of non-search shortcuts (navigate to
 * dashboards, run one-click platform ops) shown when the input is empty OR
 * when the query prefix looks like a command keyword. Zero-typing navigation.
 */
import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { toast } from 'sonner';
import {
  CommandDialog,
  CommandInput,
  CommandList,
  CommandEmpty,
  CommandGroup,
  CommandItem,
  CommandSeparator,
} from '@/components/ui/command';
import {
  UserCircle, Calendar, GraduationCap, Users, Church, BookOpen,
  Heart, Wallet, Home, HelpingHand, Search,
  BarChart3, Layers, Shield, FileText, Bell, Activity, Zap,
  ArrowRight,
} from 'lucide-react';
import { getToken } from '@/lib/authToken';

const API = process.env.REACT_APP_BACKEND_URL;

const KIND_META = {
  person:        { label: 'People',     icon: UserCircle },
  event:         { label: 'Events',     icon: Calendar },
  delegate:      { label: 'Delegates',  icon: Users },
  student:       { label: 'Students',   icon: GraduationCap },
  church_member: { label: 'Members',    icon: Users },
  church:        { label: 'Churches',   icon: Church },
  book:          { label: 'Books',      icon: BookOpen },
  prayer:        { label: 'Prayer',     icon: HelpingHand },
  transaction:   { label: 'Finance',    icon: Wallet },
  room:          { label: 'Rooms',      icon: Home },
};

// ── Quick Actions — curated non-search shortcuts ──────────────────
// Each entry can either navigate (route) or run an action (onRun).
// keywords[] powers matching when the user types a hint (e.g. "exec").
const QUICK_ACTIONS = [
  {
    id: 'exec-dashboard',
    label: 'Go to Executive Dashboard',
    subtitle: 'Ecosystem-wide KPIs · people · revenue · workspaces',
    keywords: ['executive', 'dashboard', 'kpi', 'exec', 'summary'],
    icon: BarChart3,
    route: '/admin/executive',
  },
  {
    id: 'doc-audit',
    label: 'Open Document Audit Ledger',
    subtitle: 'Every PDF/QR/badge produced across the ecosystem',
    keywords: ['document', 'audit', 'ledger', 'pdf', 'docs'],
    icon: FileText,
    route: '/admin/documents/audit',
  },
  {
    id: 'dup-audit',
    label: 'Open Duplication Audit',
    subtitle: 'Files bypassing shared platform services',
    keywords: ['duplication', 'audit', 'migration', 'platform'],
    icon: Layers,
    route: '/admin/audit/duplication',
  },
  {
    id: 'church-audit',
    label: 'Open Church Workspace Audit',
    subtitle: 'Platform-adoption score per church route file',
    keywords: ['church', 'audit', 'workspace', 'platform'],
    icon: Church,
    route: '/admin/audit/church',
  },
  {
    id: 'church-hg-split',
    label: 'Home Group Split View (FWM HQ)',
    subtitle: 'Drag-and-drop bulk home-group reassignment',
    keywords: ['home', 'group', 'split', 'drag', 'reassign', 'members', 'bulk'],
    icon: Home,
    // FWM HQ is the primary church tenant; deep-links can pass any churchId.
    route: '/admin/churches/69b670a61027d5e895ba51b8/home-group-split',
  },
  {
    id: 'leadership-console',
    label: 'Open Leadership Governance Console',
    subtitle: 'Positions · Appointments · Delegations · Succession',
    keywords: ['leadership', 'governance', 'archbishop', 'bishop', 'pastor', 'positions', 'appointments'],
    icon: Shield,
    route: '/admin/leadership',
  },
  {
    id: 'church-ops-hub',
    label: 'Open Church Ops Hub',
    subtitle: 'Campuses · Families · Visitor follow-up pipeline',
    keywords: ['campus', 'family', 'families', 'visitor', 'follow-up', 'property'],
    icon: Shield,
    route: '/admin/church-ops',
  },
  {
    id: 'team-permissions',
    label: 'Open Team Permissions',
    subtitle: 'Grant / revoke capabilities per admin — includes revenue visibility',
    keywords: ['team', 'permissions', 'roles', 'capabilities', 'access', 'admins', 'revenue', 'finance'],
    icon: Shield,
    route: '/admin/team-permissions',
  },
  {
    id: 'worship-hub',
    label: 'Open Faith Works Worship',
    subtitle: 'Songs · Worship sets · Service planning · Live presentation',
    keywords: ['worship', 'songs', 'presentation', 'projector', 'service', 'scripture', 'set', 'planner'],
    icon: Shield,
    route: '/admin/worship',
  },
  {
    id: 'system-events',
    label: 'Open Workflow Event Explorer',
    subtitle: 'System events · replay · listener status',
    keywords: ['workflow', 'events', 'explorer', 'timeline'],
    icon: Activity,
    route: '/admin/system-events',
  },
  {
    id: 'security-console',
    label: 'Open Security Console',
    subtitle: 'Admin inventory · rotate defaults · finance toggle',
    keywords: ['security', 'console', 'rotate', 'admin'],
    icon: Shield,
    route: '/admin/security/console',
  },
  {
    id: 'persons',
    label: 'Browse Person Registry',
    subtitle: 'Every human across delegates · students · members · admins',
    keywords: ['persons', 'registry', 'people', 'identity'],
    icon: UserCircle,
    route: '/admin/persons',
  },
  {
    id: 'finance-ledger',
    label: 'Open Finance Ledger Explorer',
    subtitle: 'Every transaction in the unified ledger',
    keywords: ['finance', 'ledger', 'transactions', 'money'],
    icon: Wallet,
    route: '/admin/finance/ledger',
  },
  {
    id: 'emit-test-event',
    label: 'Emit test Payment_Confirmed event',
    subtitle: 'Triggers document generation + notification dispatch',
    keywords: ['emit', 'test', 'event', 'payment', 'debug'],
    icon: Zap,
    onRun: async () => {
      const token = getToken();
      if (!token) { toast.error('Not authenticated'); return; }
      try {
        const r = await axios.post(
          `${API}/api/admin/system-events/emit`,
          {
            event_type: 'Payment_Confirmed',
            workspace: 'ecosystem',
            payload: {
              transaction_id: `TEST-${Date.now()}`,
              amount: 42.00,
              currency: 'USD',
              category: 'test',
              workspace: 'ecosystem',
              name: 'Palette Test',
              email: 'palette-test@example.com',
              method: 'test',
              event_name: 'Cmd+K debug',
            },
          },
          { headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' } },
        );
        toast.success(`Test event emitted · ${r.data?.event_id || 'ok'}`);
      } catch (e) {
        toast.error(`Emit failed: ${e?.response?.data?.detail || e.message}`);
      }
    },
  },
];

export default function GlobalCommandPalette() {
  const [open, setOpen] = useState(false);
  const [q, setQ] = useState('');
  const [results, setResults] = useState([]);
  const [loading, setLoading] = useState(false);
  const [recents, setRecents] = useState([]);
  const navigate = useNavigate();

  const RECENTS_KEY = 'fwm.palette.recents.v1';
  const MAX_RECENTS = 10;

  // Load recents from localStorage
  useEffect(() => {
    try {
      const raw = localStorage.getItem(RECENTS_KEY);
      if (raw) {
        const parsed = JSON.parse(raw);
        if (Array.isArray(parsed)) setRecents(parsed.slice(0, MAX_RECENTS));
      }
    } catch { /* ignore corrupt storage */ }
  }, []);

  const rememberRecent = (entry) => {
    try {
      const next = [entry, ...recents.filter((r) => r.key !== entry.key)].slice(0, MAX_RECENTS);
      setRecents(next);
      localStorage.setItem(RECENTS_KEY, JSON.stringify(next));
    } catch { /* localStorage full or unavailable */ }
  };

  const clearRecents = () => {
    setRecents([]);
    try { localStorage.removeItem(RECENTS_KEY); } catch { /* ignore */ }
  };

  // Cmd+K / Ctrl+K toggle
  useEffect(() => {
    const onKey = (e) => {
      if ((e.key === 'k' || e.key === 'K') && (e.metaKey || e.ctrlKey)) {
        e.preventDefault();
        setOpen((v) => !v);
      }
    };
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, []);

  // Debounced search
  const search = useCallback(async (query) => {
    const token = getToken();
    if (!token || !query || query.trim().length < 2) {
      setResults([]);
      return;
    }
    setLoading(true);
    try {
      const r = await axios.get(
        `${API}/api/search/universal?q=${encodeURIComponent(query)}&limit=24`,
        { headers: { Authorization: `Bearer ${token}` } },
      );
      setResults(r.data?.items || []);
    } catch {
      setResults([]);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    if (!open) return;
    const t = setTimeout(() => search(q), 250);
    return () => clearTimeout(t);
  }, [q, open, search]);

  // Group by kind
  const grouped = results.reduce((acc, item) => {
    (acc[item.kind] = acc[item.kind] || []).push(item);
    return acc;
  }, {});

  const handleSelect = (item) => {
    // Remember this pick before closing
    rememberRecent({
      key: `search:${item.collection}:${item.id}`,
      kind: 'search',
      itemKind: item.kind,
      label: item.label,
      subtitle: item.subtitle,
      route: item.route,
    });
    setOpen(false);
    setQ('');
    if (item.route) navigate(item.route);
  };

  const runQuickAction = async (qa) => {
    rememberRecent({
      key: `qa:${qa.id}`,
      kind: 'quick_action',
      qaId: qa.id,
      label: qa.label,
      subtitle: qa.subtitle,
      route: qa.route || null,
      hasAction: !!qa.onRun,
    });
    if (qa.onRun) {
      setOpen(false);
      setQ('');
      await qa.onRun();
      return;
    }
    setOpen(false);
    setQ('');
    if (qa.route) navigate(qa.route);
  };

  const runRecent = async (r) => {
    // Re-run the same target: quick action → find and run; search → navigate to stored route
    if (r.kind === 'quick_action') {
      const qa = QUICK_ACTIONS.find((x) => x.id === r.qaId);
      if (qa) return runQuickAction(qa);
    }
    if (r.route) {
      rememberRecent(r); // bumps it to the top
      setOpen(false);
      setQ('');
      navigate(r.route);
    }
  };

  // Filter quick actions by query prefix ("action mode")
  const query = q.trim().toLowerCase();
  const matchedQuickActions = query.length === 0
    ? QUICK_ACTIONS
    : QUICK_ACTIONS.filter((qa) =>
        qa.label.toLowerCase().includes(query) ||
        qa.subtitle.toLowerCase().includes(query) ||
        qa.keywords.some((k) => k.includes(query) || query.includes(k))
      );

  // Show recents only when the input is empty
  const showRecents = query.length === 0 && recents.length > 0;

  return (
    <CommandDialog open={open} onOpenChange={setOpen} data-testid="global-command-palette">
      <CommandInput
        placeholder="Search or run an action…  (Cmd+K)"
        value={q}
        onValueChange={setQ}
        data-testid="command-palette-input"
      />
      <CommandList>
        {showRecents && (
          <CommandGroup
            heading={
              <div className="flex items-center justify-between" data-testid="palette-recents-heading">
                <span>Recent</span>
                <button
                  type="button"
                  onClick={(e) => { e.stopPropagation(); clearRecents(); }}
                  className="text-[10px] uppercase tracking-wider text-slate-500 hover:text-slate-300"
                  data-testid="palette-clear-recents"
                >
                  clear
                </button>
              </div>
            }
            data-testid="command-group-recents"
          >
            {recents.map((r) => {
              const Icon = r.kind === 'quick_action'
                ? (QUICK_ACTIONS.find((x) => x.id === r.qaId)?.icon || Zap)
                : (KIND_META[r.itemKind]?.icon || Search);
              return (
                <CommandItem
                  key={r.key}
                  value={`recent ${r.label} ${r.subtitle || ''}`}
                  onSelect={() => runRecent(r)}
                  data-testid={`palette-recent-${r.key.replace(/[^a-z0-9-]/gi, '_')}`}
                >
                  <Icon className="mr-2 h-4 w-4 text-amber-300" />
                  <div className="flex-1">
                    <div className="text-sm">{r.label}</div>
                    {r.subtitle && (
                      <div className="text-xs text-slate-500">{r.subtitle}</div>
                    )}
                  </div>
                  <span className="ml-2 text-[10px] uppercase tracking-wider text-slate-500">
                    {r.kind === 'quick_action' ? 'action' : r.itemKind}
                  </span>
                </CommandItem>
              );
            })}
          </CommandGroup>
        )}

        {showRecents && <CommandSeparator />}

        {/* Quick Actions — always visible when input is empty or a keyword matches */}
        {matchedQuickActions.length > 0 && (
          <CommandGroup heading="Quick Actions" data-testid="command-group-quick-actions">
            {matchedQuickActions.map((qa) => {
              const Icon = qa.icon || Zap;
              return (
                <CommandItem
                  key={qa.id}
                  value={`quick action ${qa.label} ${qa.subtitle} ${qa.keywords.join(' ')}`}
                  onSelect={() => runQuickAction(qa)}
                  data-testid={`quick-action-${qa.id}`}
                >
                  <Icon className="mr-2 h-4 w-4 text-violet-300" />
                  <div className="flex-1">
                    <div className="text-sm">{qa.label}</div>
                    {qa.subtitle && (
                      <div className="text-xs text-slate-500">{qa.subtitle}</div>
                    )}
                  </div>
                  <ArrowRight className="ml-2 h-3 w-3 text-slate-500" />
                </CommandItem>
              );
            })}
          </CommandGroup>
        )}

        {q.length >= 2 && results.length > 0 && <CommandSeparator />}

        {!loading && q.length >= 2 && results.length === 0 && matchedQuickActions.length === 0 && (
          <CommandEmpty data-testid="command-palette-empty">No matches for &quot;{q}&quot;.</CommandEmpty>
        )}
        {q.length < 2 && (
          <div className="p-4 text-center text-xs text-slate-500" data-testid="command-palette-hint">
            <Search className="mx-auto mb-2 h-4 w-4 opacity-50" />
            Type at least 2 characters to search People, Events, Delegates,
            Students, Books, Prayers, Invoices, Rooms and Churches.
          </div>
        )}
        {loading && q.length >= 2 && (
          <div className="p-4 text-center text-xs text-slate-500">Searching…</div>
        )}
        {Object.entries(grouped).map(([kind, items]) => {
          const meta = KIND_META[kind] || { label: kind, icon: Search };
          const Icon = meta.icon;
          return (
            <CommandGroup key={kind} heading={meta.label} data-testid={`command-group-${kind}`}>
              {items.map((it) => (
                <CommandItem
                  key={`${it.collection}-${it.id}`}
                  value={`${meta.label} ${it.label} ${it.subtitle} ${it.id}`}
                  onSelect={() => handleSelect(it)}
                  data-testid={`command-item-${it.kind}-${it.id}`}
                >
                  <Icon className="mr-2 h-4 w-4 opacity-70" />
                  <div className="flex-1">
                    <div className="text-sm">{it.label}</div>
                    {it.subtitle && (
                      <div className="text-xs text-slate-500">{it.subtitle}</div>
                    )}
                  </div>
                  <span className="ml-2 text-xs text-slate-500">{it.kind}</span>
                </CommandItem>
              ))}
            </CommandGroup>
          );
        })}
      </CommandList>
    </CommandDialog>
  );
}
